import {
  useLayoutContext
} from "./chunk-HFINPXUM.mjs";
import {
  SIDEBAR_DRAWER_SIZE
} from "./chunk-65W2CFPO.mjs";

// src/sidebar.tsx
import { Logout as LogoutIcon } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useBoolean } from "@clara/hooks";
import { CrossIcon, ExpandMenuIcon, CollapseMenuIcon } from "@clara/icons";
import { useT } from "@clara/locale";
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerHeader,
  DrawerFooter,
  SideNavigation,
  InternalButton,
  Logo,
  CloseButton
} from "@clara/ui";
import { noop } from "@clara/utils";
import { jsx, jsxs } from "react/jsx-runtime";
function Sidebar(props) {
  const {
    items,
    onChange,
    activePredicate,
    mapItem,
    onSignOutClick = noop,
    signOutLabel = "sign out",
    ...rest
  } = props;
  const { sidebar } = useLayoutContext();
  const drawer = sidebar.getDrawerProps();
  const navigationProps = {
    items,
    onChange,
    activePredicate,
    mapItem,
    isCollapsed: sidebar.isCollapsed
  };
  return /* @__PURE__ */ jsxs(
    Drawer,
    {
      size: sidebar.sm ? SIDEBAR_DRAWER_SIZE.sm : sidebar.isCollapsed ? SIDEBAR_DRAWER_SIZE.collapsed : SIDEBAR_DRAWER_SIZE.lg,
      component: "aside",
      PaperProps: {
        sx: () => ({
          bgcolor: "v3.sidenav.default",
          color: "common.white",
          scrollbarWidth: "thin",
          scrollbarColor: "#59687F #1F304B",
          "::-webkit-scrollbar": { height: "8px", width: "8px" },
          "::-webkit-scrollbar-track": {
            borderRadius: "0px",
            backgroundColor: "#1F304B"
          },
          "::-webkit-scrollbar-track:hover": { backgroundColor: "#3D4F6C" },
          "::-webkit-scrollbar-track:active": { backgroundColor: "#3D4F6C" },
          "::-webkit-scrollbar-thumb": {
            borderRadius: "8px",
            backgroundColor: "#59687F"
          },
          "::-webkit-scrollbar-thumb:hover": { backgroundColor: "#8491A6" },
          "::-webkit-scrollbar-thumb:active": { backgroundColor: "#8491A6" }
        })
      },
      ...drawer,
      ...rest,
      children: [
        /* @__PURE__ */ jsxs(
          DrawerHeader,
          {
            "data-testid": "clara-header",
            sx: {
              p: 2,
              color: "common.white",
              bgcolor: "secondary.main",
              borderBottom: "none",
              pr: sidebar.sm && !sidebar.isCollapsed ? 1.5 : sidebar.isCollapsed ? 2 : 1,
              flexDirection: !sidebar.sm && sidebar.isCollapsed ? "column" : "row"
            },
            children: [
              /* @__PURE__ */ jsx(
                Logo,
                {
                  href: "/",
                  sx: {
                    width: 24,
                    height: 24,
                    "&:hover": {
                      color: "unset"
                    }
                  },
                  color: "common.white"
                }
              ),
              sidebar.sm ? /* @__PURE__ */ jsx(DrawerCloseButton, { colorScheme: "whiteAlpha", color: "inherit", children: /* @__PURE__ */ jsx(CrossIcon, { fontSize: "medium" }) }) : /* @__PURE__ */ jsx(
                CollapseDrawerButton,
                {
                  sx: {
                    ...sidebar.isCollapsed && {
                      mt: 3
                    }
                  }
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(DrawerBody, { px: 0, pt: 1, children: /* @__PURE__ */ jsx(SideNavigation, { ...navigationProps }) }),
        /* @__PURE__ */ jsx(
          DrawerFooter,
          {
            px: 0,
            py: 0,
            justifyContent: "flex-start",
            alignItems: "stretch",
            flexDirection: "column",
            borderTop: "1px solid",
            borderColor: "v3.primary.hover",
            children: /* @__PURE__ */ jsx(
              InternalButton,
              {
                leftIcon: /* @__PURE__ */ jsx(LogoutIcon, { fontSize: sidebar.isCollapsed ? "large" : "small" }),
                ...sidebar.isCollapsed && { iconSpacing: 0 },
                variant: "unstyled",
                sx: (theme) => ({
                  ...theme.typography.body2,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  paddingInlineStart: theme.space["4"],
                  paddingInlineEnd: theme.space["3"],
                  paddingBlock: theme.space["1"],
                  minHeight: theme.space["14"],
                  borderRadius: 0,
                  "&:hover": {
                    bgcolor: "v3.sidenav.hover"
                  }
                }),
                onClick: onSignOutClick,
                children: sidebar.isCollapsed ? null : signOutLabel
              }
            )
          }
        )
      ]
    }
  );
}
function CollapseDrawerButton(props) {
  const { sidebar } = useLayoutContext();
  const { t } = useT("common");
  const [isOpen, setOpen] = useBoolean();
  const buttonProps = sidebar.getCollapseButtonProps({
    ...props,
    onClick: () => setTimeout(() => setOpen.off(), 250)
  });
  const Icon = sidebar.isCollapsed ? ExpandMenuIcon : CollapseMenuIcon;
  const offsetX = !sidebar.isCollapsed ? 4 : 12;
  const title = `sidebar.collapse.${sidebar.isCollapsed ? "on" : "off"}`;
  return /* @__PURE__ */ jsx(
    Tooltip,
    {
      open: isOpen,
      disableInteractive: true,
      title: /* @__PURE__ */ jsx(Typography, { variant: "caption", children: t(title) }),
      placement: "right",
      onClose: setOpen.off,
      onOpen: setOpen.on,
      slotProps: {
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 4 + offsetX]
              }
            }
          ]
        }
      },
      children: /* @__PURE__ */ jsx(CloseButton, { colorScheme: "whiteAlpha", color: "inherit", ...buttonProps, children: /* @__PURE__ */ jsx(Icon, { fontSize: "medium" }) })
    }
  );
}

export {
  Sidebar
};
